/* eslint-disable prettier/prettier */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO, isBefore, startOfToday, addHours } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import Input from '../../components/Input';
import SelectSync from '../../components/SelectSync';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import Agendamento from '../../components/Agendamento';
import Paginacao from '../../components/Paginacao';

import ItemTabela from './ItemTabela';

import getValidationErrors from '../../utils/getValidationErrors';

import { useAgenda } from '../../hooks/agenda';

import getResponseErrors from '../../utils/getResponseErrors';

import {
  optionsStatusAgendamento,
  formataTelefone,
  STATUS_AGENDA,
} from '../../utils/funcoes';
import { IReturnInstanceInfo } from '../Whatsapp';

export interface IAgendamento {
  AgeCodigo: number;
  AgeChave: string;
  AgeConfCodigo: number;
  AgeInicio: Date;
  AgeFim: Date;
  AgeDataFormatada: string;
  AgeHoraFormatada: string;
  AgeOrigem: number;
  AgeStatus: number;
  AgeConfirmado: number;
  AgeStatusFormatado: string;
  PacNome: string;
  PacTelefone: string;
  PacCelular: string;
  PacEmail: string;
  PacTelefonesFormatado: string;
  ConNome: string;
  PerNome: string;
  DataUltimaNotificacao?: Date;
  DataUltimaNotificacaoFormatado: string;
  textoMensagem: string;
  textoDigisacFormatado: string;
}

export interface IAgendamentoSelecionado {
  AgeCodigo: number;
  AgeConfCodigo: number;
}

interface IFormBusca {
  AgeConfCodigo: number;
  AgeInicio: Date;
}

interface IPaginacao {
  AgeConfCodigo?: number;
  AgeInicio: Date;
  dataFake: Date;
  limite: number;
  pagina: number;
}

export interface IOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

const AgendaEnvioConfirmacao: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);

  const [showAgendamento, setShowAgendamento] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState<
    IAgendamentoSelecionado | undefined
  >(undefined);
  const [agendamentos, setAgendamentos] = useState<IAgendamento[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const { retornaDadosAgenda } = useAgenda();
  const [optionsAgenda, setOptionsAgenda] = useState<IOption[]>([]);

  const [instanceInfo, setInstanceInfo] = useState<IReturnInstanceInfo>({
    error: true,
    message: 'não conectado',
  });

  useEffect(() => {
    async function loadDadosInstancia(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IReturnInstanceInfo>('whatsapp/status');
        setInstanceInfo(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    loadDadosInstancia();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      const options = await retornaDadosAgenda();
      setOptionsAgenda(options);
    }
    loadDados();
  }, [retornaDadosAgenda]);

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const initialData = useMemo(() => {
    let AgeInicio = format(new Date(), 'yyyy-MM-dd');
    if (query.get('data') && query.get('data')?.length === 10) {
      AgeInicio = format(
        parseISO(`${query.get('data')} 03:00:00`),
        'yyyy-MM-dd',
      );
    }

    const AgeConfCodigo = query.get('agenda') || 0;

    return { AgeInicio, AgeConfCodigo };
  }, [query]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeConfCodigo: Yup.number()
            .nullable()
            .transform(x => x || null),
          AgeInicio: Yup.date().required('Início precisa ser uma data'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setAgendamentos(undefined);

        const { AgeInicio, AgeConfCodigo } = dados;

        const ageInicio = parseISO(AgeInicio.toString());

        if (isBefore(ageInicio, startOfToday())) {
          addToast({
            title: 'Confirmação não está disponível para datas passadas',
            type: 'error',
            description: 'A data mínima é o dia atual',
          });
          return;
        }

        setPaginacao({
          ...(AgeConfCodigo.toString().length > 0
            ? { AgeConfCodigo: Number(AgeConfCodigo) }
            : {}),
          AgeInicio: parseISO(AgeInicio.toString()),
          dataFake: new Date(),
          pagina: 1,
          limite: 20,
        });
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        }
      }
    },
    [addToast],
  );

  useEffect(() => {
    formRefBusca.current?.submitForm();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;

        let parametros = '';
        const { AgeInicio, AgeConfCodigo } = paginacao;
        if (AgeConfCodigo) parametros += `&ConCodigo=${AgeConfCodigo}`;

        const InicioFormatado = format(AgeInicio, 'yyyy-MM-dd');

        const response = await api.get(
          `/agendamentos/buscaparaconfirmacao?&pagina=${paginacao.pagina}&limite=${paginacao.limite}&AgeInicio=${InicioFormatado}${parametros}`,
        );
        const responseApi: IAgendamento[] = response.data[0];

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        const agendamentosFormatados = responseApi.map(age => {
          const inicio = parseISO(age.AgeInicio.toString());

          const DataUltimaNotificacaoFormatado = age.DataUltimaNotificacao
            ? format(
              addHours(parseISO(age.DataUltimaNotificacao.toString()), -3),
              'dd/MM/yyyy HH:mm',
            )
            : '';

          const PacTelefonesFormatado =
            age.PacTelefone && age.PacTelefone.length > 0
              ? `${formataTelefone(age.PacCelular)} - ${formataTelefone(
                age.PacTelefone,
              )}`
              : formataTelefone(age.PacCelular);

          return {
            ...age,
            AgeInicio: inicio,
            AgeDataFormatada: format(inicio, 'dd/MM/yyyy'),
            AgeHoraFormatada: `${format(inicio, 'HH:mm')} - ${format(
              parseISO(age.AgeFim.toString()),
              'HH:mm',
            )}`,
            DataUltimaNotificacaoFormatado,
            AgeStatusFormatado:
              optionsStatusAgendamento.find(sta =>
                age.AgeStatus === STATUS_AGENDA.AGENDADO
                  ? sta.value ===
                  (age.AgeConfirmado + 1 >= 2 ? 2 : 1).toString()
                  : sta.value === age.AgeStatus.toString(),
              )?.label || 'vazio',
            PacTelefonesFormatado,
          };
        });

        setAgendamentos(agendamentosFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar agendamentos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => { }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      {showAgendamento && agendamentoSelecionado && (
        <Modal
          show={showAgendamento}
          titulo="Agendamento"
          handleFecharModal={
            () => {
              setAgendamentoSelecionado(undefined);
              setShowAgendamento(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <Agendamento
            agendaId={agendamentoSelecionado.AgeConfCodigo}
            perfilId={undefined}
            data={undefined}
            AgeCodigo={agendamentoSelecionado.AgeCodigo}
            handleFecharAgendamento={agenda => {
              setShowAgendamento(false);
              setPaginacao(state => {
                return state ? { ...state, dataFake: new Date() } : undefined;
              });
            }}
          />
        </Modal>
      )}
      <div className="mb-2 p-2 bg-white border rounded">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 id="topoBusca">Envio de Confirmação de Agendamentos</h6>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => history.push('/agendas')}
          >
            Voltar para agendas
          </Button>
        </div>

        <Form
          ref={formRefBusca}
          initialData={initialData}
          onSubmit={handleSubmitBusca}
        >
          <Row noGutters>
            <Col sm={6} md={3} lg={2} className="pr-1">
              <Input name="AgeInicio" label="Data" type="date" />
            </Col>
            <Col sm={12} md={4} lg={2} className="pr-1">
              <SelectSync
                name="AgeConfCodigo"
                label="Agenda"
                handleSelectChange={handleOnChangeVazio}
                options={optionsAgenda}
              />
            </Col>
            <Col sm={12} md={3} lg={1} className="pr-1">
              <Button
                type="submit"
                size="sm"
                className="mt-3"
                variant="primary"
              >
                BUSCAR
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="bg-white border p-2 rounded">
        {agendamentos ? (
          <>
            <b>Total de {totalRegistros} registros</b>
            <Row noGutters className="bg-light py-2 border-top border-bottom">
              <Col md={2} lg={1}>
                <b>Data</b>
              </Col>
              <Col md={2} lg={1}>
                <b>Horário</b>
              </Col>
              <Col md={3} lg={2}>
                <b>Agenda</b>
              </Col>
              <Col md={3} lg={2}>
                <b>Paciente</b>
              </Col>
              <Col md={2} lg={2}>
                <b>Telefone</b>
              </Col>
              <Col md={2} lg={1}>
                <b>Status</b>
              </Col>
              <Col md={2} lg={1}>
                <b>Último Envio</b>
              </Col>
              <Col md={2} lg={1}>
                -
              </Col>
              <Col md={2} lg={1}>
                -
              </Col>
            </Row>
            {agendamentos.map(age => (
              <ItemTabela
                agendamento={age}
                key={age.AgeCodigo}
                conectado={instanceInfo.instance_data?.phone_connected || false}
              />
            ))}
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Envie uma mensagem de confirmação para o paciente através do
              Whatsapp.
            </h5>
          </div>
        )}

        <Paginacao
          limitePagina={paginacao?.limite || 20}
          paginasVizinhas={2}
          totalRegistros={totalRegistros}
          paginaInicio={paginacao?.pagina}
          handleProximaPagina={handleProximaPagina}
        />
      </div>
    </Container>
  );
};

export default AgendaEnvioConfirmacao;
